import { SplideProps, SplideSlide } from '@splidejs/react-splide'
import { Splide } from '@splidejs/splide'
import clsx from 'clsx'
import filter from 'lodash/filter'
import indexOf from 'lodash/indexOf'
import map from 'lodash/map'
import noop from 'lodash/noop'
import throttle from 'lodash/throttle'
import React, {
  ChangeEventHandler,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
  VFC,
} from 'react'
import styled from 'styled-components'

import { getPersonalizationTranslationHeader, ProductPersonalization } from '@Components/Personalization/Modals/Shared'
import { CrossSellItem } from '@Components/Product/CrossSells/CrossSellItem'
import { TWO_FOR_ONE_DISCOUNT_PERCENTAGE } from '@Components/Product/Display/Info'
import { SizeGuideWrapper } from '@Components/Product/SizeGuide/styledElements'
import { ContainerSlider } from '@Components/Slider/ContainerSlider'
import { useSplideProgressBar } from '@Components/Slider/utils/useSplideProgressBar'
import { Product } from '@Generated/graphql'
import { getCrossSellsForProduct, getUniqProductPersonalizations } from '@Globals/CMS/ProductHelpers'
import { Experiment } from '@Globals/SplitTests/Experiment'
import { up } from '@Globals/Style/breakpoints'
import theme from '@Globals/Style/theme'
import { useTranslation } from '@Globals/Translations'
import useEffectLoopInterval from '@Globals/useEffectInterval'
import { useMediaQuery } from '@Globals/useMediaQuery'

export const CrossSellWrapper = styled.section<{ sliderEnabled?: boolean }>(({ sliderEnabled }) => ({
  borderTop: 'none',
  borderBottom: sliderEnabled ? undefined : `5px solid ${theme.colors.greyLightest}`,
  margin: '0',
  padding: `10px 0 ${sliderEnabled ? '0' : '20px'}`,

  'input:checked ~ .pdp-checkbox': {
    background: theme.colors.primary,
    borderColor: theme.colors.primary,
    color: theme.colors.white,
  },

  [`${SizeGuideWrapper} + &`]: {
    borderTop: `1px solid ${theme.colors.greyLighter}`,
    marginTop: '2px',
    paddingTop: 20,
  },

  '.uv-print': {
    img: {
      objectFit: 'contain',
    },
  },

  [up('mobileStyleThreshold')]: {
    marginBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.greyLighter,
  },
}))

const SliderWrapper = styled.div(({
  overflow: 'hidden',
  position: 'relative',
}))

type CrossSellsProps = {
  buyPersonalizationId?: string | null
  buyTwoAllowed?: boolean
  buyTwoPrice?: string
  buyTwoSelected?: boolean
  className?: string
  onBuyPersonalizationId?: (personalizationId: string | null) => void
  onBuyTwoChanged?: (newState: boolean) => void
  product: Product
  selectedIDs?: Product['id'][]
  setSelectedIDs?: SetStateAction<any>
}

enum SliderStates {
  AT_BEGINNING,
  IN_MIDDLE,
  AT_END,
}

export const CrossSells: VFC<CrossSellsProps> = ({
  buyPersonalizationId,
  buyTwoPrice,
  buyTwoSelected,
  className,
  onBuyTwoChanged = noop,
  onBuyPersonalizationId = noop,
  buyTwoAllowed,
  product,
  selectedIDs = [],
  setSelectedIDs,
}) => {
  const deviceIsLargeDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.wide})`)
  const deviceIsDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.mobileStyleThreshold})`)
  const deviceIsTablet = useMediaQuery(`(min-width: ${theme.breakpoints.tablet})`)
  const deviceIsPhablet = useMediaQuery(`(min-width: ${theme.breakpoints.phablet})`)
  const [personalizations] = useState<ProductPersonalization[]>(getUniqProductPersonalizations(product))
  const availableCrossSells = useMemo(
    () => getCrossSellsForProduct(product),
    [product],
  )
  let availableCrossSellsSize = availableCrossSells.length + (buyTwoAllowed ? 1 : 0)
  if (personalizations?.length) {
    availableCrossSellsSize += personalizations.length
  }

  const perPage = useMemo(() => {
    if (deviceIsLargeDesktop) {
      return 4
    }

    if (deviceIsDesktop) {
      return 3
    }

    if (deviceIsTablet) {
      return 6
    }

    if (deviceIsPhablet) {
      return 4
    }

    return 3
  }, [deviceIsDesktop, deviceIsLargeDesktop, deviceIsPhablet, deviceIsTablet])

  const { t } = useTranslation()
  const isSelected = useCallback((id: Product['id']): boolean => indexOf(selectedIDs, id) >= 0, [selectedIDs])
  const [currentSliderState, setCurrentSliderState] = useState(SliderStates.AT_BEGINNING)
  const [splideInstance, setSpliceInstance] = useState<Splide>()
  const {
    dragging,
    isEnabled: progressBarEnabled,
    onSliderMounted: mountProgressBar,
    progressBar,
    setDragging,
    update: updateProgressBar,
  } = useSplideProgressBar({ allowOvershoot: true, perPage })

  const onBuyTwoChangeEvent: ChangeEventHandler<HTMLInputElement> = useCallback(({ currentTarget: { checked } }) => {
    onBuyTwoChanged(checked)
  }, [onBuyTwoChanged])

  const onBuyPersonalizationChangeEvent: ChangeEventHandler<HTMLInputElement> = useCallback(({
    currentTarget: {
      checked,
      value,
    },
  }) => {
    if (checked) {
      onBuyPersonalizationId(value)
    } else {
      onBuyPersonalizationId(null)
    }
  }, [onBuyPersonalizationId])

  const onCheckboxChange: ChangeEventHandler<HTMLInputElement> = useCallback(({
    currentTarget: {
      checked,
      value,
    },
  }) => {
    if (!setSelectedIDs) {
      return
    }

    if (checked) {
      setSelectedIDs([...selectedIDs, value])
    } else {
      setSelectedIDs(filter(selectedIDs, currentID => currentID !== value))
    }
  }, [selectedIDs, setSelectedIDs])

  const onSliderMoved: SplideProps['onMoved'] = useCallback((splide, newIndex) => {
    if (newIndex === 0) {
      setCurrentSliderState(SliderStates.AT_BEGINNING)
    } else if (newIndex + perPage >= availableCrossSellsSize) {
      setCurrentSliderState(SliderStates.AT_END)
    } else {
      setCurrentSliderState(SliderStates.IN_MIDDLE)
    }

    updateProgressBar(splide)
  }, [perPage, availableCrossSellsSize, updateProgressBar])

  const onSliderMounted: SplideProps['onMounted'] = useCallback((splide) => {
    setSpliceInstance(splide)
    mountProgressBar(splide)
  }, [mountProgressBar])

  const onSliderResized = useCallback((splide: Splide) => {
    // This might seem stupid. Why not just use the "perPage" lookup as we usually do, instead of looking at the options
    // we set later on? Because apparently this event listener callback isn't updated in the Splide instance when it's
    // updated, and I just can't be arsed to tell them that they need to fix their shit.
    // This works, and is actually less code on our side anyway, so... well, I mean, it works.
    if (!splide.options.drag) {
      // The reason for even doing this at all is the following use-case:
      //  1. the user has moved the slider to an index above zero,
      //  2. the user increases the screen size, the "per page" setting changes, and there aren't enough items to enable sliding, so
      //  3. the slider cannot be dragged (because we change the "drag" setting), and is locked in place, so we can't see all items
      // We simply go back to the first slide in this case, and Bob's your uncle.
      splide.go(0)
    }

    updateProgressBar(splide)
  }, [updateProgressBar])

  useEffect(() => {
    if (splideInstance) {
      updateProgressBar(splideInstance)
    }
  }, [updateProgressBar, splideInstance, perPage])

  useEffectLoopInterval((intervalID) => {
    if (splideInstance && dragging) {
      updateProgressBar(splideInstance)
    }
    if (!dragging) {
      clearInterval(intervalID)
    }
  }, { msInterval: 24, easeIntervals: 10 }, [dragging])

  if (!availableCrossSellsSize) {
    return null
  }

  const horizontalScroll = (event) => {
    const { deltaY, type, wheelDeltaX, wheelDeltaY } = event.nativeEvent
    if (type === 'wheel' && splideInstance && Math.abs(wheelDeltaX) > Math.abs(wheelDeltaY)) {
      throttle(
        () => {
          const moveLeft = wheelDeltaX > 0
          const currentIndex = splideInstance!.index
          const newIndex = moveLeft ? currentIndex - 1 : currentIndex + 1
          splideInstance!.go(newIndex)
          updateProgressBar(splideInstance)
        },
        150,
      )()
    } else if (type === 'wheel' && Math.abs(wheelDeltaX) < Math.abs(wheelDeltaY)) {
      window.scrollTo({ top: document.documentElement.scrollTop + deltaY })
    }
  }

  return (
    <CrossSellWrapper
      className={className}
      sliderEnabled={availableCrossSellsSize > perPage}
    >
      <SliderWrapper
        className={progressBarEnabled
          ? clsx({
            'at-beginning': currentSliderState === SliderStates.AT_BEGINNING,
            'at-end': currentSliderState === SliderStates.AT_END,
            'in-middle': currentSliderState === SliderStates.IN_MIDDLE,
            'slider-enabled': true,
          })
          : undefined}
      >
        <ContainerSlider
          onDrag={() => setDragging(true)}
          onDragged={() => setDragging(false)}
          onMounted={onSliderMounted}
          onMoved={onSliderMoved}
          onResized={onSliderResized}
          onWheel={horizontalScroll}
          options={{
            arrows: availableCrossSellsSize > perPage,
            wheelMinThreshold: 999999,
            wheel: true,
            // Only allow dragging if there are enough items to do so.
            drag: availableCrossSellsSize > perPage,
            dragMinThreshold: {
              mouse: 0,
              touch: 1,
            },
            easing: 'cubic-bezier(0.215, 0.61, 0.355, 1)',
            breakpoints: undefined,
            fixedWidth: 100,
            gap: theme.padding.phablet,
            padding: deviceIsDesktop ? 0 : theme.padding.phablet,
            pagination: false,
            perMove: perPage,
            perPage,
            width: '100%',
          }}
        >

          {personalizations && (
            <Experiment
              B={(
                <>
                  {/* eslint-disable-next-line react/no-unstable-nested-components */}
                  {map(personalizations, (item) => {
                    const type = item.masterVariant.type.value.key
                    const image = type === 'uv-print'
                      ? { url: 'https://eu-images.contentstack.com/v3/assets/blt7dcd2cfbc90d45de/blt92740812a0dfbdf2/uv-image-child-in-watch.png', width: 100, height: 100 }
                      : item.primaryAsset
                    return (
                      <SplideSlide key={item.id} className={type}>
                        <CrossSellItem
                          onChange={onBuyPersonalizationChangeEvent}
                          product={{
                            id: item.id ?? '',
                            masterVariant: item.masterVariant,
                            primaryAsset: image,
                            url: null,
                            name: t(getPersonalizationTranslationHeader(item)),
                          }}
                          selected={buyPersonalizationId === item.id}
                        />
                      </SplideSlide>
                    )
                  })}
                </>
              )}
              test="new_pp_cross_design_v1"
            />
          )}

          {buyTwoAllowed && (
            <SplideSlide>
              <CrossSellItem
                onChange={onBuyTwoChangeEvent}
                product={{
                  id: product.id,
                  url: '',
                  name: t('products:PP_BUY_TWO_AND_SAVE_X', { arg1: TWO_FOR_ONE_DISCOUNT_PERCENTAGE }),
                  // @ts-ignore
                  primaryAsset: product.primaryAsset,
                  masterVariant: {
                    ...product.masterVariant,
                    // @ts-ignore - we don't care about any missing properties here.
                    price: {
                      ...product.masterVariant?.price,
                      format: buyTwoPrice,
                    },
                  },
                }}
                selected={buyTwoSelected}
              />
            </SplideSlide>
          )}
          {map(availableCrossSells, item => (
            <SplideSlide key={item.id}>
              <CrossSellItem
                onChange={onCheckboxChange}
                product={item}
                selected={isSelected(item.id)}
              />
            </SplideSlide>
          ))}
        </ContainerSlider>
      </SliderWrapper>
      {progressBar}
    </CrossSellWrapper>
  )
}
